import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    IconButton,
    Tooltip,
    Chip,
    CircularProgress,
    Alert,
    Snackbar,
    Typography,
    Card,
    CardContent,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Grid,
    Fade,
    Skeleton,
    Button,
    useTheme,
    alpha
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Download as DownloadIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Receipt as ReceiptIcon,
    CalendarToday as CalendarIcon,
    AccountBalance as AccountBalanceIcon,
    TrendingUp as TrendingUpIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../config/api';

const Invoices = () => {
    const theme = useTheme();
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [dateFilter, setDateFilter] = useState('ALL');

    // Statistics
    const [stats, setStats] = useState({
        totalInvoices: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0
    });

    useEffect(() => {
        fetchInvoices();
    }, []);

    useEffect(() => {
        filterInvoices();
        calculateStats();
    }, [invoices, searchTerm, statusFilter, dateFilter]);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const response = await api.get('/invoices');
            setInvoices(response.data);
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to fetch invoices');
            showSnackbar('Failed to fetch invoices', 'error');
        } finally {
            setLoading(false);
        }
    };

    const filterInvoices = () => {
        let filtered = [...invoices];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(invoice =>
                invoice.id.toString().includes(searchTerm) ||
                invoice.contract_id.toString().includes(searchTerm) ||
                invoice.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Status filter
        if (statusFilter !== 'ALL') {
            filtered = filtered.filter(invoice => invoice.status === statusFilter);
        }

        // Date filter
        if (dateFilter !== 'ALL') {
            const now = new Date();
            const filterDate = new Date();

            switch (dateFilter) {
                case 'THIS_MONTH':
                    filterDate.setMonth(now.getMonth());
                    filtered = filtered.filter(invoice =>
                        new Date(invoice.created_date).getMonth() === now.getMonth() &&
                        new Date(invoice.created_date).getFullYear() === now.getFullYear()
                    );
                    break;
                case 'LAST_30_DAYS':
                    filterDate.setDate(now.getDate() - 30);
                    filtered = filtered.filter(invoice =>
                        new Date(invoice.created_date) >= filterDate
                    );
                    break;
                case 'LAST_90_DAYS':
                    filterDate.setDate(now.getDate() - 90);
                    filtered = filtered.filter(invoice =>
                        new Date(invoice.created_date) >= filterDate
                    );
                    break;
            }
        }

        setFilteredInvoices(filtered);
        setPage(0); // Reset to first page when filtering
    };

    const calculateStats = () => {
        const totalAmount = invoices.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0);
        const paidAmount = invoices
            .filter(inv => inv.status === 'PAID')
            .reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0);

        setStats({
            totalInvoices: invoices.length,
            totalAmount,
            paidAmount,
            pendingAmount: totalAmount - paidAmount
        });
    };

    const handleViewInvoice = async (invoicePath) => {
        if (!invoicePath) {
            showSnackbar('Invoice file is not available', 'warning');
            return;
        }

        try {
            const response = await api.get(`/invoices/view/${invoicePath}`, {
                responseType: 'blob'
            });

            const file = new Blob([response.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL, '_blank');
            showSnackbar('Invoice opened successfully', 'success');
        } catch (error) {
            console.error('Error viewing invoice:', error);
            showSnackbar('Failed to view invoice. The file might not exist.', 'error');
        }
    };

    const handleDownloadInvoice = async (invoicePath, invoiceId) => {
        if (!invoicePath) {
            showSnackbar('Invoice file is not available', 'warning');
            return;
        }

        try {
            const response = await api.get(`/invoices/download/${invoicePath}`, {
                responseType: 'blob'
            });

            const file = new Blob([response.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = `Invoice-${invoiceId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(fileURL);
            showSnackbar('Invoice downloaded successfully', 'success');
        } catch (error) {
            console.error('Error downloading invoice:', error);
            showSnackbar('Failed to download invoice. The file might not exist.', 'error');
        }
    };

    const showSnackbar = (message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatCurrency = (amount) => {
        return `$${parseFloat(amount || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PAID': return 'success';
            case 'PENDING': return 'warning';
            case 'OVERDUE': return 'error';
            default: return 'default';
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    {[1, 2, 3, 4].map((item) => (
                        <Grid item xs={12} sm={6} md={3} key={item}>
                            <Card>
                                <CardContent>
                                    <Skeleton variant="text" width="60%" height={24} />
                                    <Skeleton variant="text" width="40%" height={32} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress size={48} />
                            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                                Loading invoices...
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={fetchInvoices}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Fade in={true} timeout={300}>
            <Box sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                        Invoice Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage and track all your invoices in one place
                    </Typography>
                </Box>

                {/* Statistics Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            color: 'white'
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            Total Invoices
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                            {stats.totalInvoices}
                                        </Typography>
                                    </Box>
                                    <ReceiptIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                            color: 'white'
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            Total Amount
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                            {formatCurrency(stats.totalAmount)}
                                        </Typography>
                                    </Box>
                                    <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                            color: 'white'
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            Paid Amount
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                            {formatCurrency(stats.paidAmount)}
                                        </Typography>
                                    </Box>
                                    <AccountBalanceIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                            color: 'white'
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            Pending Amount
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                            {formatCurrency(stats.pendingAmount)}
                                        </Typography>
                                    </Box>
                                    <CalendarIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Filters */}
                <Paper sx={{ p: 3, mb: 3, background: alpha(theme.palette.primary.main, 0.02) }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                placeholder="Search invoices..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="ALL">All Status</MenuItem>
                                    <MenuItem value="PAID">Paid</MenuItem>
                                    <MenuItem value="PENDING">Pending</MenuItem>
                                    <MenuItem value="OVERDUE">Overdue</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Date Range</InputLabel>
                                <Select
                                    value={dateFilter}
                                    label="Date Range"
                                    onChange={(e) => setDateFilter(e.target.value)}
                                >
                                    <MenuItem value="ALL">All Time</MenuItem>
                                    <MenuItem value="THIS_MONTH">This Month</MenuItem>
                                    <MenuItem value="LAST_30_DAYS">Last 30 Days</MenuItem>
                                    <MenuItem value="LAST_90_DAYS">Last 90 Days</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={fetchInvoices}
                                size="small"
                            >
                                Refresh
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Main Table */}
                <Paper sx={{ overflow: 'hidden', borderRadius: 2 }}>
                    <TableContainer>
                        <Table sx={{ minWidth: 750 }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Invoice Number
                                    </TableCell>
                                   
                                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Contract Number
                                    </TableCell>
                                    {/* <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Package
                                    </TableCell> */}
                                    {/* <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Package Name
                                    </TableCell> */}
                                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Customer Name
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Company Name
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Amount
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Status
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Created Date
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Payment Date
                                    </TableCell>
                                    {/* <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Due Date
                                    </TableCell> */}
                                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredInvoices
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((invoice, index) => (
                                        <TableRow
                                            key={invoice.id}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: alpha(theme.palette.action.hover, 0.04)
                                                },
                                                '&:hover': {
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    INV-{invoice.id}
                                                </Typography>
                                            </TableCell>
                                         
                                            <TableCell>
                                                <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                                                    CNT-{invoice.contract_id}
                                                </Typography>
                                            </TableCell>
                                            {/* <TableCell>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {invoice.package_name || invoice.subscription_package_name || invoice.package || '\u2014'}
                                                    </Typography>
                                                    {invoice.package_description && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {invoice.package_description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell> */}
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {invoice.customer_name || invoice.customerName || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {invoice.company_name || invoice.companyName || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {formatCurrency(invoice.amount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={invoice.status}
                                                    color={getStatusColor(invoice.status)}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        minWidth: '80px',
                                                        textTransform: 'capitalize'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(invoice.created_date)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {invoice.payment_date ? formatDate(invoice.payment_date) : '-'}
                                                </Typography>
                                            </TableCell>
                                            {/* <TableCell>
                                                <Typography variant="body2">
                                                    {invoice.due_date || invoice.expiration_date || invoice.invoice_due_date || invoice.expirationDate ? formatDate(invoice.due_date || invoice.expiration_date || invoice.invoice_due_date || invoice.expirationDate) : '-'}
                                                </Typography>
                                            </TableCell> */}
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Tooltip title={invoice.invoice_path ? "View Invoice" : "Invoice not available"}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleViewInvoice(invoice.invoice_path)}
                                                                disabled={!invoice.invoice_path}
                                                                sx={{
                                                                    color: theme.palette.primary.main,
                                                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                                                                }}
                                                            >
                                                                <VisibilityIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={invoice.invoice_path ? "Download Invoice" : "Invoice not available"}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDownloadInvoice(invoice.invoice_path, invoice.id)}
                                                                disabled={!invoice.invoice_path}
                                                                sx={{
                                                                    color: theme.palette.success.main,
                                                                    '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.1) }
                                                                }}
                                                            >
                                                                <DownloadIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                {filteredInvoices.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <ReceiptIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
                                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                                    No invoices found
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {searchTerm || statusFilter !== 'ALL' || dateFilter !== 'ALL'
                                                        ? 'Try adjusting your filters'
                                                        : 'Create your first invoice to get started'
                                                    }
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {filteredInvoices.length > 0 && (
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={filteredInvoices.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                borderTop: `1px solid ${theme.palette.divider}`,
                                backgroundColor: alpha(theme.palette.action.hover, 0.02)
                            }}
                        />
                    )}
                </Paper>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                        variant="filled"
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Fade>
    );
};

export default Invoices;